import algoliasearch from 'algoliasearch/lite';
import { useMemo } from 'react';
import { useInstantSearchContext } from 'react-instantsearch';

import type {
  SearchFacetDefinitionInput,
  SearchFilterInput,
  SearchRefinementInput,
} from '__generated__/globalTypes';
import { SearchFaceting, SearchOperator } from '__generated__/globalTypes';
import { useConfigContext } from 'contexts/config';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { getAlgoliaHosts } from 'lib/algolia';

export const useSearchClient = () => {
  const { algoliaApplicationId, algoliaSearchApiKey } = useConfigContext();
  const {
    flags: { useAlgoliaGeoOptimizedReadReplica = false },
  } = useFeatureFlags();

  const searchClient = useMemo(() => {
    return algoliasearch(algoliaApplicationId, algoliaSearchApiKey, {
      hosts: getAlgoliaHosts(
        algoliaApplicationId,
        useAlgoliaGeoOptimizedReadReplica
      ),
    });
  }, [
    algoliaApplicationId,
    algoliaSearchApiKey,
    useAlgoliaGeoOptimizedReadReplica,
  ]);

  return searchClient;
};

const OPERATORS: Record<string, SearchOperator> = {
  '=': SearchOperator.EQUAL,
  '>': SearchOperator.GREATER_THAN,
  '>=': SearchOperator.GREATER_THAN_OR_EQUAL,
  '<': SearchOperator.LESS_THAN,
  '<=': SearchOperator.LESS_THAN_OR_EQUAL,
  '!=': SearchOperator.NOT_EQUAL,
};

export class SorareSearchClient {
  // we assume that all facet filters & all numeric filters are coming from disjunctive facets
  protected toRefinements(
    facetFilters: readonly string[][],
    numericFilters: readonly string[] | undefined
  ): SearchRefinementInput[] {
    // Flatten all values and group by field
    const fieldMap = new Map<string, { stringValue: string }[]>();
    facetFilters?.flat().forEach(value => {
      const [field, v] = value.split(':');
      if (!field || v === undefined) return;
      if (!fieldMap.has(field)) fieldMap.set(field, []);
      fieldMap.get(field)!.push({ stringValue: v });
    });

    const res: SearchFilterInput[] = Array.from(fieldMap.entries()).map(
      ([field, values]) => ({
        field,
        operator: SearchOperator.EQUAL,
        values,
      })
    );

    if (numericFilters) {
      numericFilters.forEach(numericFilter => {
        const match = numericFilter.match(
          /^([._\-\w]+)(!=|=|<|>|>=|<=)([-0-9.]+)$/
        );
        if (match) {
          const [, field, operator, value] = match;
          res.push({
            field,
            operator: OPERATORS[operator]!,
            values: [
              {
                integerValue: parseInt(value, 10),
              },
            ],
          });
        }
      });
    }
    return res;
  }

  protected toFacets(
    facets: readonly string[] | undefined
  ): SearchFacetDefinitionInput[] {
    return (
      facets?.map(facet => ({
        field: facet,
        limit: 30,
        type: SearchFaceting.DISJUNCTIVE, // consider all facets disjunctive
      })) || []
    );
  }
}

export const useIsSorareSearchClient = () => {
  const { client } = useInstantSearchContext();
  return client instanceof SorareSearchClient;
};
